import dayjs from 'dayjs';
import { useSizeTypes } from 'contexts';
import { FC } from 'react';

import { ActiveUnit, EquipmentType } from 'types';

type ContainerDetailsProps = Pick<ActiveUnit, 'equipmentType' | 'loaded' | 'seal0' | 'seal1' | 'seal2' | 'hazard'>;
const ContainerDetails: FC<ContainerDetailsProps> = container => {
  if (container.equipmentType !== EquipmentType.CONTAINER) return null;
  return (
    <div className="card-primary">
      <div className="card-header px-3">
        <h4>CONTAINER DETAILS</h4>
      </div>
      <div className="card-body">
        <div className="grid-6 p-2">
          <div>
            <h6 className="text-dark font-weight-regular">LOADED</h6>
            <p className="text-black">{!container.loaded ? 'NO' : 'YES'}</p>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">SEAL #1</h6>
            <p className="text-black">{container.seal0 ?? 'N/A'}</p>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">SEAL #2</h6>
            <p className="text-black">{container.seal1 ?? 'N/A'}</p>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">SEAL #3</h6>
            <p className="text-black">{container.seal2 ?? 'N/A'}</p>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">HAZARD</h6>
            <p className="text-black">{!container.hazard ? 'NO' : 'YES'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

type ReeferDetailsProps = Pick<ActiveUnit, 'equipmentType' | 'sizeType' | 'temperature' | 'humidity' | 'ventilation'>;
const ReeferDetails: FC<ReeferDetailsProps> = reefer => {
  const { isReefer } = useSizeTypes();
  if (reefer.equipmentType !== EquipmentType.CONTAINER || !isReefer(reefer.sizeType)) return null;
  return (
    <div className="card-primary">
      <div className="card-header px-3">
        <h4>REEFER DETAILS</h4>
      </div>
      <div className="card-body">
        <div className="grid-6 p-2">
          <div>
            <h6 className="text-dark font-weight-regular">TEMPERATURE</h6>
            <p className="text-black">{typeof reefer.temperature !== 'number' ? 'N/A' : `${reefer.temperature}°C`}</p>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">HUMIDITY</h6>
            <p className="text-black">{typeof reefer.humidity !== 'number' ? 'N/A' : `${reefer.humidity}%`}</p>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">VENTILATION</h6>
            <p className="text-black">{!reefer.ventilation ? 'N/A' : `${reefer.ventilation}%`}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

type ChassisDetailsProps = Pick<
  ActiveUnit,
  | 'equipmentType'
  | 'licensePlate'
  | 'licensePlateExpirationDate'
  | 'fmcsaDate'
  | 'flatbed'
  | 'ownerChassis'
  | 'sizeType'
>;
const ChassisDetails: FC<ChassisDetailsProps> = chassis => {
  if (chassis.equipmentType !== EquipmentType.CHASSIS && !chassis.sizeType?.endsWith('TR')) return null;
  return (
    <div className="card-primary">
      <div className="card-header px-3">
        <h4>CHASSIS DETAILS</h4>
      </div>
      <div className="card-body">
        <div className="grid-6">
          <div>
            <h6 className="text-dark font-weight-regular">LICENSE PLATE</h6>
            <p className="text-black">{chassis.licensePlate ?? 'N/A'}</p>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">LP EXPIRATION</h6>
            <p className="text-black">
              {!chassis.licensePlateExpirationDate
                ? 'N/A'
                : dayjs.utc(chassis.licensePlateExpirationDate).local().format('LL')}
            </p>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">FMCSA DATE</h6>
            <p className="text-black">
              {!chassis.fmcsaDate ? 'N/A' : dayjs.utc(chassis.fmcsaDate).local().format('LL')}
            </p>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">FLATBED</h6>
            <p className="text-black">{!chassis.flatbed ? 'NO' : 'YES'}</p>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">OWNER</h6>
            <p className="text-black">{!chassis.ownerChassis ? 'NO' : 'YES'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

type GensetDetailsProps = Pick<ActiveUnit, 'equipmentType' | 'fuelLevel' | 'carbCompliant'>;
const GensetDetails: FC<GensetDetailsProps> = genset => {
  if (genset.equipmentType !== EquipmentType.GENSET) return null;
  return (
    <div className="card-primary">
      <div className="card-header px-3">
        <h4>GENSET DETAILS</h4>
      </div>
      <div className="card-body">
        <div className="grid-6">
          <div>
            <h6 className="text-dark font-weight-regular">FUEL LEVEL</h6>
            <p className="text-black">{typeof genset.fuelLevel !== 'number' ? 'N/A' : `${genset.fuelLevel}/4`}</p>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">CARB COMPLIANT</h6>
            <p className="text-black">{!genset.carbCompliant ? 'NO' : 'YES'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const EquipmentDetails: FC<Partial<ActiveUnit>> = unit => {
  return (
    <>
      <ContainerDetails {...unit} />
      <ReeferDetails {...unit} />
      <ChassisDetails {...unit} />
      <GensetDetails {...unit} />
    </>
  );
};

export default EquipmentDetails;
